import React, {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Card, Divider, Tooltip, Typography} from "@mui/material";
import {InfoOutlined} from "@mui/icons-material";
import ConfigurableInfoBlock, {ConfigurableInfoBlockInputTypes} from "../../../../../../../components/app-specific/configurable-info-block";
import useIndustry from "../../../../../../../hooks/use-industry";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";

/**
 * Personal Industry Settings card — the intentional UI for changing the operator default industry.
 */
export const IndustrySettingsSection = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.settings.personal.industry-settings'});
    const {
        industries,
        loadingIndustries,
        setDefaultIndustry,
        hasNoIndustries,
    } = useIndustry({noCategories: true});
    const isMounted = useIsMounted();

    const defaultIndustry = useMemo(
        () => industries.find((industry) => !!industry?.isDefault) ?? null,
        [industries],
    );

    const industryOptions = useMemo(
        () => industries.map((industry) => ({
            value: industry.id,
            label: industry.title,
            id: industry.id,
        })),
        [industries],
    );

    /**
     * Persists the selected industry as the operator default.
     * @type {function(string): Promise<{resultFlag: boolean, message?: string}>}
     */
    const updateDefaultIndustry = useCallback(async (industryId) => {
        if (industryId === defaultIndustry?.id) {
            return {resultFlag: true};
        }
        const industry = industries.find((item) => item.id === industryId);
        if (!industry) {
            return {resultFlag: false, message: t('updateFailed')};
        }
        const resultFlag = await setDefaultIndustry(industry);
        if (!isMounted()) {
            return;
        }
        return {
            resultFlag: !!resultFlag,
            message: resultFlag ? t('updateSuccess') : t('updateFailed'),
        };
    }, [defaultIndustry?.id, industries, isMounted, setDefaultIndustry, t]);

    return (
        <>
            {
                loadingIndustries &&
                <Grid2 xs={12}>
                    <div className={'loading access-level w-100'}>
                        <div>
                            <div/>
                        </div>
                    </div>
                </Grid2>
            }
            {
                !loadingIndustries && industries.length > 1 &&
                <Grid2 xs={12}>
                    <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                        <Grid2 container spacing={2}>
                            <Grid2 xs={12}>
                                <Typography variant={'subtitle1'} fontWeight={600}>
                                    {t('title')}
                                </Typography>
                            </Grid2>
                            <Grid2 xs={12}>
                                <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                            </Grid2>
                            {
                                hasNoIndustries &&
                                <Grid2 xs={12} paddingInline={3}>
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        {t('noIndustries')}
                                    </Typography>
                                </Grid2>
                            }
                            {
                                !hasNoIndustries &&
                                <Grid2 xs={12} lg={6} paddingInline={3}>
                                    <ConfigurableInfoBlock
                                        title={t('defaultIndustryLabel')}
                                        inputType={ConfigurableInfoBlockInputTypes.select}
                                        inputProps={{
                                            value: defaultIndustry?.id,
                                            data: industryOptions,
                                            SelectProps: {
                                                renderValue: (selectedValue) => (
                                                    <p>
                                                        {industryOptions.find((option) => option.value === selectedValue)?.label
                                                            ?? selectedValue
                                                            ?? ''}
                                                    </p>
                                                ),
                                            },
                                        }}
                                        onValueChanged={updateDefaultIndustry}
                                        showCustomIcons
                                        customIcons={[
                                            <Tooltip key={1} title={t('defaultIndustryTooltip')}>
                                                <InfoOutlined color={'primary'}/>
                                            </Tooltip>,
                                        ]}
                                    />
                                </Grid2>
                            }
                        </Grid2>
                    </Card>
                </Grid2>
            }
        </>
    );
};
